import React from 'react';
import Icon from '../../../components/AppIcon';

const QuickStats = ({ statsData }) => {
  const {
    totalRealizedGains,
    totalUnrealizedGains,
    bestPerformer,
    worstPerformer,
    diversificationScore,
    riskScore
  } = statsData;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })?.format(value);
  };

  const formatPercentage = (value) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value?.toFixed(2)}%`;
  };

  const getPerformanceColor = (value) => {
    if (value > 0) return 'text-success';
    if (value < 0) return 'text-error';
    return 'text-muted-foreground';
  };

  const getRiskColor = (score) => {
    if (score <= 3) return 'text-success';
    if (score <= 6) return 'text-warning';
    return 'text-error';
  };

  const getRiskLabel = (score) => {
    if (score <= 3) return 'Low';
    if (score <= 6) return 'Medium';
    return 'High';
  };

  const getDiversificationColor = (score) => {
    if (score >= 8) return 'text-success';
    if (score >= 6) return 'text-warning';
    return 'text-error';
  };

  const StatCard = ({ icon, title, value, subtitle, color = 'text-card-foreground', trend }) => (
    <div className="bg-card border border-border rounded-lg p-4 hover:shadow-elevation-2 transition-standard">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-primary/10 rounded-md flex items-center justify-center">
            <Icon name={icon} size={16} className="text-primary" />
          </div>
          <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
        </div>
        {trend && (
          <Icon 
            name={trend > 0 ? 'TrendingUp' : 'TrendingDown'} 
            size={16} 
            className={trend > 0 ? 'text-success' : 'text-error'} 
          />
        )}
      </div>
      <div>
        <p className={`text-lg font-semibold ${color} mb-1`}>{value}</p>
        {subtitle && (
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Quick Stats</h2>
        <button className="text-sm text-primary hover:text-primary/80 transition-standard">
          View Detailed Report
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <StatCard
          icon="TrendingUp"
          title="Realized Gains"
          value={formatCurrency(totalRealizedGains)}
          color={getPerformanceColor(totalRealizedGains)}
          trend={totalRealizedGains}
          subtitle="From completed trades"
        />

        <StatCard
          icon="Eye"
          title="Unrealized P&L"
          value={formatCurrency(totalUnrealizedGains)}
          color={getPerformanceColor(totalUnrealizedGains)}
          trend={totalUnrealizedGains}
          subtitle="Current open positions"
        />

        <StatCard
          icon="Award"
          title="Best Performer"
          value={bestPerformer?.symbol}
          color="text-success"
          subtitle={`${formatPercentage(bestPerformer?.performance)} gain`}
          trend={bestPerformer?.performance}
        />

        <StatCard
          icon="AlertTriangle"
          title="Worst Performer"
          value={worstPerformer?.symbol}
          color="text-error"
          subtitle={`${formatPercentage(worstPerformer?.performance)} loss`}
          trend={worstPerformer?.performance}
        />

        <StatCard
          icon="PieChart"
          title="Diversification"
          value={`${diversificationScore}/10`}
          color={getDiversificationColor(diversificationScore)}
          subtitle="Portfolio spread"
        />

        <StatCard
          icon="Shield"
          title="Risk Level"
          value={getRiskLabel(riskScore)}
          color={getRiskColor(riskScore)}
          subtitle={`Score: ${riskScore}/10`}
        />
      </div>
      {/* Risk Metrics */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="text-sm font-medium text-card-foreground mb-4">Risk Metrics</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Portfolio Beta</span>
            <span className="text-sm font-medium text-card-foreground">1.24</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Sharpe Ratio</span>
            <span className="text-sm font-medium text-card-foreground">0.87</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Max Drawdown</span>
            <span className="text-sm font-medium text-error">-15.3%</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Value at Risk (95%)</span>
            <span className="text-sm font-medium text-warning">$8,450</span>
          </div>
        </div>
      </div>
      {/* Tax Summary */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-card-foreground">Tax Summary (2024)</h3>
          <button className="text-xs text-primary hover:text-primary/80 transition-standard">
            Generate Report
          </button>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Short-term Gains</span>
            <span className="text-sm font-medium text-success">$12,450</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Long-term Gains</span>
            <span className="text-sm font-medium text-success">$8,920</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Total Losses</span>
            <span className="text-sm font-medium text-error">-$3,200</span>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <span className="text-sm font-medium text-card-foreground">Est. Tax Liability</span>
            <span className="text-sm font-medium text-warning">$4,850</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickStats;